import PropTypes from 'prop-types';
import { ReactComponent as AddIcon } from 'assets/plus-circle-icon.svg';
import AmountInput from 'uikit/AmountInput';
import { ActionButton } from 'uikit/ActionButton/ActionButton';
import i18n from 'i18n';
import { AMOUNT_INPUT_VALUE_TYPE, COMMON_CURRENCY_PRECISION } from 'components/constants';
import OriginalCommission, { formatTariffRule, originalCommissionPropType } from './OriginalCommission';
import './index.scss';

const CommissionsList = ({
  commissions,
  onChange,
  onBlur,
  onDelete,
  onAdd,
  errors,
  amountTitle = 'Amount',
  isShowRowCurrency = true,
  isShowCount = true,
  isShowTariffRule = false,
  originalCommission,
  precision = COMMON_CURRENCY_PRECISION,
  currency: transactionCurrency
}) => {
  // Always show the commission component, even if no commissions
  const displayCommissions = commissions && commissions.length > 0 ? commissions : [];

  // Calculate total commission
  const total = displayCommissions.reduce((sum, commission) => {
    const amount = parseFloat(commission.amount) || 0;
    return sum + amount;
  }, 0);

  // The transaction currency is used when it is passed - the list can be empty
  const currency = transactionCurrency || displayCommissions[0]?.currency || 'EUR';

  const renderAddButton = () => (
    <button type='button' className='add-commission-btn' onClick={onAdd}>
      <AddIcon className='add-commission-btn-icon' aria-hidden='true' />
      {i18n.getMessage('createTransaction.payment.commission.addCustom')}
    </button>
  );

  const renderTariffRule = (commission) => {
    const rule = formatTariffRule(commission, precision);
    return rule && <span className='tariff-rule'>{rule}</span>;
  };

  return (
    <div className='commissions-list-container'>
      <div className='commissions-list-header'>
        <span className='commissions-title'>{i18n.getMessage('createTransaction.payment.commission.label')}</span>
        {isShowCount && (
          <span className='commissions-count'>
            {displayCommissions.length} {displayCommissions.length === 1 ? 'commission' : 'commissions'}
          </span>
        )}
      </div>

      {originalCommission && (
        <OriginalCommission commission={originalCommission} currency={currency} precision={precision} />
      )}

      <div className='commissions-table'>
        <div className='commissions-table-header'>
          <div className='column-tariff'>Tariff</div>
          <div className='column-amount'>{amountTitle}</div>
        </div>

        <div className='commissions-table-body'>
          {displayCommissions.length === 0 ? (
            <div className='no-commissions-message'>
              <p>{i18n.getMessage('createTransaction.payment.commission.noCommissions')}</p>
            </div>
          ) : (
            displayCommissions.map((commission, index) => (
              <div key={`commission-${index}`} className='commission-row'>
                <div className={isShowTariffRule ? 'column-tariff with-tariff-rule' : 'column-tariff'}>
                  <span className='tariff-name'>
                    {commission.isCustom || !commission.tariffId
                      ? 'Custom Commission'
                      : commission.label ||
                        commission.tariffName ||
                        commission.systemTariffKey ||
                        `Tariff ID: ${commission.tariffId}` ||
                        `Commission ${index + 1}`}
                  </span>
                  {commission.highRiskCountries && (
                    <span className='high-risk-badge' title='High Risk Country Tariff'>
                      HR
                    </span>
                  )}
                  {isShowTariffRule
                    ? renderTariffRule(commission)
                    : commission.feeType && (
                        <span className='fee-type-badge'>{commission.feeType === 'PERCENT' ? '%' : 'F'}</span>
                      )}
                  <input type='hidden' name={`commissions[${index}].tariffId`} value={commission.tariffId || ''} />
                </div>
                <div className='column-amount'>
                  <div className='amount-input-wrapper'>
                    <AmountInput
                      className='commission-amount-input'
                      placeholder='0.00'
                      name={`commissions[${index}].amount`}
                      value={commission.amount}
                      returnValueType={AMOUNT_INPUT_VALUE_TYPE.STRING}
                      onChange={(e) => onChange(index, e.target.value)}
                      onBlur={() => onBlur(index)}
                      error={errors?.[index]?.amount}
                    />
                    {isShowRowCurrency && <span className='currency-label'>{currency}</span>}
                    <ActionButton
                      className='delete-commission-btn'
                      iconType='delete'
                      onClick={() => onDelete && onDelete(index)}
                      title='Delete commission'
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className='commissions-list-footer'>
          <div className='footer-left'>{onAdd && renderAddButton()}</div>
          <div className='footer-right'>
            <span className='total-label'>Total</span>
            <span className='total-value'>
              {total.toFixed(2)} {currency}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

CommissionsList.propTypes = {
  commissions: PropTypes.arrayOf(
    PropTypes.shape({
      tariffId: PropTypes.number,
      tariffName: PropTypes.string,
      systemTariffKey: PropTypes.string,
      feeType: PropTypes.oneOf(['FIXED', 'PERCENT']),
      highRiskCountries: PropTypes.bool,
      type: PropTypes.string,
      label: PropTypes.string,
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      currency: PropTypes.string
    })
  ),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onAdd: PropTypes.func,
  errors: PropTypes.array,
  amountTitle: PropTypes.string,
  // The currency can be hidden in rows when it is already shown in the amount title
  isShowRowCurrency: PropTypes.bool,
  isShowCount: PropTypes.bool,
  // The tariff rule (e.g. "Fixed · 6.00") is shown under the tariff name instead of the fee type badge
  isShowTariffRule: PropTypes.bool,
  // Commission of the original transaction, shown for reference above the list
  originalCommission: originalCommissionPropType,
  // Precision of the original commission and the tariff rules amounts
  precision: PropTypes.number,
  // Commissions are charged in the transaction currency
  currency: PropTypes.string
};

export default CommissionsList;
